import React from "react";

import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { push } from "connected-react-router";
import { all, call, put, select, takeLatest, takeLeading } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Route, {
  PreviewRoutePayload,
  RoutePreview,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import Organization from "@mapmycustomers/shared/types/Organization";

import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import buildPreviewFromStoredRoute from "@app/scene/routes/utils/buildPreviewFromStoredRoute";
import { buildRoutePreviewMetrics } from "@app/scene/routes/utils/buildRouteLegMetrics";
import mergeRoute, { RoutePatch } from "@app/scene/routes/utils/mergeRoute";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { routeValidationErrorMessages } from "@app/store/routes/messages";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { findDifference } from "@app/util/arrayUtils";
import buildPreviewPayloadFromRoute from "@app/util/route/buildPreviewPayloadFromRoute";
import getRemainingRouteStops from "@app/util/route/getRemainingRouteStops";
import isRouteComplete from "@app/util/route/isRouteComplete";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";

import { transferStops } from "../../moveStops/actions";
import {
  deleteRoute,
  rebuildRoutePreview,
  resetAllStops,
  setRoute,
  setRouteStopChecked,
  shareRoute,
  updateRoute,
} from "../actions";
import { messages } from "../messages";
import sagaStyles from "../saga.module.scss";
import { getRoute, getRoutePreview } from "../selectors";

const requiresPreview = (patch: RoutePatch): boolean => {
  if (!patch.routeDetail) {
    return false;
  }
  return (
    "startAt" in patch.routeDetail ||
    "travelMode" in patch.routeDetail ||
    "type" in patch.routeDetail
  );
};

export function* fetchRouteAndPreview(routeId: Route["id"]) {
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const route: Route = yield callApi("fetchUnifiedRoute", orgId, routeId);
  // A route with no stop left to visit cannot be previewed — the BE rejects an empty
  // stopTime[] (v0280), and checked-off stops are excluded from the payload. Skip the
  // request and leave the preview undefined.
  if (getRemainingRouteStops(route).length === 0) {
    return { route, routePreview: undefined };
  }
  // The route already carries the last solve — polyline, totals and the per-stop legs the BE
  // projects from routeDetail.legMetrics. When that solve still describes this drive, render it
  // instead of buying an identical one.
  const storedPreview: RoutePreview | undefined = buildPreviewFromStoredRoute(route);
  if (storedPreview) {
    return { route, routePreview: storedPreview };
  }
  const previewPayload: PreviewRoutePayload = buildPreviewPayloadFromRoute(route);
  // The preview is a non-fatal enrichment (travel metrics + polyline). A route can be
  // unroutable — e.g. stops across a closed border return v0281 — yet still exist and be
  // worth viewing. Report the validation (v0281 → "No route could be found…") but still
  // return the route so the scene renders, instead of failing the open to a blank panel.
  try {
    const routePreview: RoutePreview = yield callApi("previewUnifiedRoute", orgId, previewPayload);
    return { route, routePreview };
  } catch (previewError) {
    yield put(
      handleError({
        defaultMessage: messages.previewUnavailable,
        error: previewError,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
    return { route, routePreview: undefined };
  }
}

export function* onUpdateRoute({ payload }: ReturnType<typeof updateRoute.request>) {
  const route: Route | undefined = yield select(getRoute);
  if (!route) {
    yield put(updateRoute.failure());
    return;
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `routeUpdate_${route.id}`;
  const merged = mergeRoute(route, payload.patch);
  const complete = isRouteComplete(route.stopTime);
  const willPreview =
    requiresPreview(payload.patch) && (complete || getRemainingRouteStops(route).length > 0);

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(
      willPreview ? messages.updateRebuildPending : messages.updatePending
    ),
  });

  let routePreview: RoutePreview | undefined;
  if (willPreview) {
    try {
      routePreview = yield callApi(
        "previewUnifiedRoute",
        orgId,
        buildPreviewPayloadFromRoute(
          {
            routeDetail: (merged.routeDetail as Route["routeDetail"]) ?? route.routeDetail,
            stopTime: route.stopTime,
          },
          { allStops: complete }
        )
      );
    } catch (error) {
      // A completed route saves today with no preview at all, so a failed solve must not start
      // blocking the edit. The BE clears the metrics on this PUT anyway, which is today's outcome.
      if (!complete) {
        notification.close(key);
        yield put(updateRoute.failure());
        yield put(
          handleError({
            defaultMessage: messages.updateFailure,
            error,
            messageByErrorCodeMap: routeValidationErrorMessages,
          })
        );
        return;
      }
      yield put(handleError({ displayNotification: false, error }));
    }
  }

  if (routePreview) {
    merged.routeDetail = { ...merged.routeDetail, ...getRoutePreviewMetrics(routePreview) };
  }

  let updated: Route;
  try {
    updated = yield callApi("updateUnifiedRoute", orgId, route.id, merged);
  } catch (error) {
    notification.close(key);
    yield put(updateRoute.failure());
    yield put(
      handleError({
        defaultMessage: messages.updateFailure,
        error,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
    return;
  }

  notification.success({
    key,
    message: intl?.formatMessage(messages.updateSuccess),
  });
  yield put(
    updateRoute.success({ route: updated, routePreview: complete ? undefined : routePreview })
  );
}

export function* onSetRouteStopChecked({
  payload,
}: ReturnType<typeof setRouteStopChecked.request>) {
  const route: Route | undefined = yield select(getRoute);
  if (!route) {
    yield put(setRouteStopChecked.failure());
    return;
  }

  const { checkedOff, entityId, entityType } = payload;
  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `setStopChecked_${entityType}_${entityId}`;

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(messages.stopSaving),
  });

  const targetStop = route.stopTime?.find(
    (stop) =>
      (stop.entity === EntityType.COMPANY &&
        entityType === EntityType.COMPANY &&
        stop.accountId === entityId) ||
      (stop.entity === EntityType.PERSON &&
        entityType === EntityType.PERSON &&
        stop.contactId === entityId) ||
      (stop.entity === null && entityType === null && stop.id === entityId)
  );
  if (!targetStop) {
    notification.close(key);
    yield put(setRouteStopChecked.failure());
    return;
  }

  // No metrics envelope: a batch of UPDATEs that touches none of the geometry keys is
  // geometry-neutral, so the BE skips the routeDetail patch entirely and every stored metric
  // survives untouched. Sending one would be worse than useless — an envelope that omits
  // `legMetrics` (which it does whenever no stop paired to the preview) reads as "clear", and
  // would null the very leg array we want kept for a later un-check.
  let updated: Route;
  try {
    yield callApi("bulkUpdateUnifiedRouteStops", orgId, route.id, [
      { id: targetStop.id, _action_: "u", checkedOff },
    ]);
    updated = yield callApi("fetchUnifiedRoute", orgId, route.id);
  } catch (error) {
    notification.close(key);
    yield put(setRouteStopChecked.failure());
    yield put(handleError({ defaultMessage: messages.stopSavingFailure, error }));
    return;
  }

  notification.success({ key, message: intl?.formatMessage(messages.stopSavingSuccess) });
  yield put(setRouteStopChecked.success({ route: updated }));
}

export function* onResetAllStops() {
  const route: Route | undefined = yield select(getRoute);
  if (!route) {
    yield put(resetAllStops.failure());
    return;
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `resetAllStops_${route.id}`;

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(messages.resetStopsPending),
  });

  let current: Route;
  let routePreview: RoutePreview | undefined;
  try {
    // Re-read the route rather than trusting the store: this action derives its target set
    // instead of acting on one stop the rep named, so a snapshot predating a check-off made
    // on another device would leave those stops done while the toast claims a full reset.
    current = yield callApi("fetchUnifiedRoute", orgId, route.id);
    const checkedOffStops = (current.stopTime ?? []).filter((stop) => stop.checkedOff);
    if (checkedOffStops.length === 0) {
      // Nothing is checked off any more — reset elsewhere. Report success.
      notification.info({ key, message: intl?.formatMessage(messages.resetStopsNothingToDo) });
      yield put(resetAllStops.success({ route: current }));
      return;
    }

    const resetStops = (current.stopTime ?? []).map((stop) => ({ ...stop, checkedOff: false }));
    // Solve the restored drive before the write, so the envelope below can carry it. A reset
    // leaves the stored legs describing the old drive, which reads back as stale.
    try {
      routePreview = yield callApi(
        "previewUnifiedRoute",
        orgId,
        buildPreviewPayloadFromRoute({ routeDetail: current.routeDetail, stopTime: resetStops })
      );
    } catch (previewError) {
      // Unroutable stops must not block the reset. Without an envelope the batch stays
      // geometry-neutral, so the stored solve survives and the rep can re-build.
      yield put(handleError({ displayNotification: false, error: previewError }));
    }

    const stopEdits: RouteStopBulkItem[] = checkedOffStops.map((stop) => ({
      id: stop.id,
      _action_: "u",
      checkedOff: false,
    }));

    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      orgId,
      route.id,
      stopEdits,
      routePreview ? buildRoutePreviewMetrics(resetStops, routePreview) : undefined
    );
  } catch (error) {
    notification.close(key);
    yield put(resetAllStops.failure());
    yield put(handleError({ defaultMessage: messages.resetStopsFailure, error }));
    return;
  }

  let updated: Route;
  try {
    updated = yield callApi("fetchUnifiedRoute", orgId, route.id);
  } catch (error) {
    // Read-back failed; the write landed, so reflect it locally.
    updated = {
      ...current,
      stopTime: (current.stopTime ?? []).map((stop) => ({ ...stop, checkedOff: false })),
    };
    yield put(handleError({ displayNotification: false, error }));
  }

  notification.success({ key, message: intl?.formatMessage(messages.resetStopsSuccess) });
  yield put(resetAllStops.success({ route: updated, routePreview }));
  yield put(notifyAboutChanges({ entityType: EntityType.ROUTE, updatedIds: [route.id] }));
}

export function* onRebuildRoutePreview() {
  const route: Route | undefined = yield select(getRoute);
  // A route with no stops has nothing to solve. A route whose stops are all checked off does:
  // that is the completion case, and it prices the whole drive.
  if (!route || !route.stopTime?.length) {
    yield put(rebuildRoutePreview.failure());
    return;
  }

  const complete = isRouteComplete(route.stopTime);

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `routeRebuild_${route.id}`;

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(messages.rebuildPending),
  });

  try {
    const routePreview: RoutePreview = yield callApi(
      "previewUnifiedRoute",
      orgId,
      buildPreviewPayloadFromRoute(route, { allStops: complete })
    );
    // Persist the fresh metrics through the bulk-stops envelope rather than the route-level PUT.
    // The PUT cannot carry `legMetrics` (400 v0159 on key presence alone) and clears whatever is
    // stored, so a rebuild used to leave the route with totals but no legs and send the next open
    // straight back to a preview — the one saving this whole feature is meant to buy.
    //
    // The envelope rides a batch of bare updates: the endpoint rejects an empty `routeGroups`
    // (v0160), so every current stop is restated as a no-op `{id, _action_: "u"}`. Carrying no
    // geometry key makes the batch geometry-neutral, which is what stops the BE from treating it
    // as a stop change and clearing the very metrics it is delivering. Stop ids must come from
    // the route we just read: an id the BE cannot match rejects the whole call (v0935).
    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      orgId,
      route.id,
      route.stopTime.map((stop) => ({ id: stop.id, _action_: "u" as const })),
      // Route-level metrics only, and omitting `legMetrics` is what nulls the stale leg array —
      // stale legs beside fresh full-route totals read as current, so an un-check would draw the lot.
      complete
        ? getRoutePreviewMetrics(routePreview)
        : buildRoutePreviewMetrics(route.stopTime, routePreview)
    );
    // The write responses carry no projected metrics — the BE only projects `legMetrics` onto
    // stops on an ES-served read — so the route has to be re-read for the scene to see them.
    const updated: Route = yield callApi("fetchUnifiedRoute", orgId, route.id);
    notification.success({ key, message: intl?.formatMessage(messages.rebuildSuccess) });
    // A completed route renders no preview, in either mode. Handing one to the store would draw
    // the whole route on a page that is meant to show the approximate line.
    yield put(
      rebuildRoutePreview.success({
        route: updated,
        routePreview: complete ? undefined : routePreview,
      })
    );
  } catch (error) {
    notification.close(key);
    yield put(rebuildRoutePreview.failure());
    yield put(
      handleError({
        defaultMessage: messages.rebuildFailure,
        error,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
  }
}

export function* onDeleteRoute() {
  const route: Route | undefined = yield select(getRoute);
  if (!route) {
    yield put(deleteRoute.failure());
    return;
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `deleteRoute_${route.id}`;

  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(messages.deletePending),
  });

  try {
    yield callApi("deleteUnifiedRoute", orgId, route.id);
  } catch (error) {
    notification.close(key);
    yield put(deleteRoute.failure());
    yield put(handleError({ defaultMessage: messages.deleteFailure, error }));
    return;
  }

  notification.success({ key, message: intl?.formatMessage(messages.deleteSuccess) });
  yield put(deleteRoute.success());
  yield put(notifyAboutChanges({ deletedIds: [route.id], entityType: EntityType.ROUTE }));
  yield put(push(Path.ROUTE));
}

export function* onRefreshSourceAfterTransfer({
  payload,
}: ReturnType<typeof transferStops.success>) {
  if (!payload.sourceRoute) {
    return;
  }
  const sourceId = payload.sourceRoute.id;
  // Only refresh when the transferred-from route is the one currently open —
  // skip the two API calls otherwise (transfers can be initiated from unrelated
  // screens).
  const current: Route | undefined = yield select(getRoute);
  if (current?.id !== sourceId) {
    return;
  }
  try {
    const { route, routePreview } = yield call(fetchRouteAndPreview, sourceId);
    yield put(setRoute({ route, routePreview }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onShareRoute({ payload }: ReturnType<typeof shareRoute.request>) {
  const route: Route | undefined = yield select(getRoute);
  if (!route) {
    yield put(shareRoute.failure());
    return;
  }

  const [toAdd, toRemove] = findDifference(route.userIds, payload.userIds);
  if (toAdd.length === 0 && toRemove.length === 0) {
    yield put(shareRoute.success({ route }));
    return;
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const intl = i18nService.getIntl();
  const key = `routeShare_${route.id}`;
  notification.info({
    icon: <FontAwesomeIcon className={sagaStyles.notificationLoader} icon={faSpinnerThird} spin />,
    key,
    message: intl?.formatMessage(messages.sharePending),
  });

  try {
    yield all([
      toAdd.length
        ? callApi(
            "bulkCreateRouteShare",
            orgId,
            toAdd.map((userId) => ({ routeId: route.id, userId }))
          )
        : undefined,
      toRemove.length
        ? callApi(
            "bulkDeleteRouteShare",
            orgId,
            toRemove.map((userId) => ({ routeId: route.id, userId }))
          )
        : undefined,
    ]);
  } catch (error) {
    notification.close(key);
    // Best-effort re-converge: a half-applied diff leaves the store out of sync.
    // Refetch the route (userIds is async-derived on the BE, so this may briefly
    // be stale — acceptable), preserving the existing preview.
    try {
      const routePreview: RoutePreview | undefined = yield select(getRoutePreview);
      const fresh: Route = yield callApi("fetchUnifiedRoute", orgId, route.id);
      yield put(setRoute({ route: fresh, routePreview }));
    } catch {
      // ignore refetch failure
    }
    yield put(shareRoute.failure());
    yield put(handleError({ defaultMessage: messages.shareFailure, error }));
    return;
  }

  notification.success({ key, message: intl?.formatMessage(messages.shareSuccess) });
  // Optimistic — BE userIds is eventually consistent; trust the just-sent set.
  yield put(shareRoute.success({ route: { ...route, userIds: payload.userIds } }));
}

export function* viewSaga() {
  yield all([
    takeLatest(updateRoute.request, onUpdateRoute),
    takeLatest(setRouteStopChecked.request, onSetRouteStopChecked),
    // takeLeading: a second confirm while the batch is in flight would re-send stops the
    // first run already cleared, and the refetch of the loser would overwrite the winner.
    takeLeading(resetAllStops.request, onResetAllStops),
    // takeLeading (not takeLatest): each press spends a paid preview + PUT. The UI already
    // disables the button while rebuilding, but takeLeading also closes the window where a
    // cancelled saga would leave a completed PUT unreflected in the store.
    takeLeading(rebuildRoutePreview.request, onRebuildRoutePreview),
    takeLatest(deleteRoute.request, onDeleteRoute),
    takeLatest(shareRoute.request, onShareRoute),
    takeLatest(transferStops.success, onRefreshSourceAfterTransfer),
  ]);
}
